import React from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { toast, Flip } from "react-toastify";
import { LinearProgress, TextField, Slider } from "@material-ui/core";
import Loading from "../../components/Loading";
import AssetPicker from "../../components/AssetPicker";
import MusicAssetPicker from "../../components/MusicAssetPicker";
import { addAsset, addMusicAsset } from "../../Redux/Actions/asset";
import { updateVideo, cleanSingleVideo } from "../../Redux/Actions/videos";

import { v4 as uuid } from "uuid";
import canvasTxt from "canvas-txt";
import { CompactPicker } from "react-color";
import { AuthState } from "../../Redux/Types/auth";
import _ from "lodash";
import { Storage } from "aws-amplify";
import { localStoragePut, localStorageGet } from "../../util/localStorage";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const storageGet = (key: string, opts: any): Promise<any> =>
  isLocalAuth ? localStorageGet(key, opts) : Storage.get(key, opts);
const storagePut = (key: string, file: any, opts: any): Promise<any> =>
  isLocalAuth ? localStoragePut(key, file, opts) : Storage.put(key, file, opts);

interface Video {
  url: string;
  thumbnail?: string;
  title?: string;
  logoProps?: any;
  textProps?: any;
  musicProps: any;
  _id: string;
  views?: number;
  watch?: number;
  emailShareCount?: number;
  recordingEdit?: boolean;
}
interface EditorProps {
  video?: Video;
  type?: string;
  toggle: () => void;
  updateVideo: (video: any) => void;
  addAsset: (asset: any) => void;
  addMusicAsset: (asset: any) => void;
  cleanSingleVideo: () => void;
  auth: AuthState;
}
interface EditState {
  file: File | null;
  url: string;
  uploading: boolean;
  isOpenLogoPicker: boolean;
  isOpenMusicPicker: boolean;
  newVideoTitle: string;
  logoPath: any;
  logoUrl: any;
  logoX: number | string;
  logoY: number | string;
  text: string;
  textColor: string;
  fontSize: number;
  vAlign: string;
  align: string;
  iconPos: string;
  assetUploading: boolean;
  videoLoaded: boolean;
  musicTitle: string;
  backgroundMusicUrl: string;
  backgroundMusicKey: string;
  backgroundMusicType: string;
  musicFileSelected: boolean;
  musicFile: any;
  musicLoadingTimeout: any;
  musicVolume: string;
  updatedThumbnailUrl: string;
  thumbnailPath: any;
  thumbnailUrl: any;
  isPlaying: boolean;
}

class VideoEditor extends React.Component<EditorProps, EditState> {
  video: any;
  backgroundMusic: any;
  logoRef: any;
  musicRef: any;
  img: any;
  canvas: any;
  dummyCanvas: any;
  ctx: any;
  ctx2: any;
  animFrame: any;

  constructor(props: any) {
    super(props);
    this.video = React.createRef();
    this.state = {
      file: null,
      url: "",
      uploading: false,
      isOpenLogoPicker: false,
      isOpenMusicPicker: false,
      newVideoTitle: "",
      logoPath: null,
      logoUrl: null,
      logoX: 10,
      logoY: 10,
      text: "",
      textColor: "#ffffff",
      fontSize: 5,
      vAlign: "top",
      align: "left",
      iconPos: "top-left",
      assetUploading: false,
      videoLoaded: false,
      musicTitle: "",
      backgroundMusicUrl: "",
      backgroundMusicKey: "",
      backgroundMusicType: "",
      musicFileSelected: false,
      musicFile: null,
      musicLoadingTimeout: null,
      musicVolume: "0.5",
      updatedThumbnailUrl: "",
      thumbnailPath: null,
      thumbnailUrl: null,
      isPlaying: false,
    };
  }

  componentDidMount() {
    this.setUpCanvasEditing();
    document.body.style.overflow = "hidden";
  }

  componentWillUnmount() {
    this.removeListeners();
    document.body.style.overflow = "";
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.state.thumbnailUrl && this.state.thumbnailUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.state.thumbnailUrl);
    }
  }

  setUpCanvasEditing = () => {
    if (this.video.current) {
      this.video = this.video.current;
      this.video.crossOrigin = "Anonymous";
      this.video.addEventListener("canplaythrough", this.handleLoadedMetaData);
      this.video.addEventListener("play", this.onVideoPlay);
      this.video.addEventListener("pause", this.onVideoPause);
      this.video.addEventListener("ended", this.onVideoEnd);
      this.video.addEventListener("volumechange", this.syncAudio);
      if (this.img) this.img.crossOrigin = "Anonymous";
      this.ctx = this.canvas?.getContext("2d");
      this.ctx2 = this.dummyCanvas?.getContext("2d");
      this.handleVideoLoaded();
      return;
    }
    setTimeout(() => this.setUpCanvasEditing(), 100);
  };

  handleVideoLoaded = async () => {
    const { video } = this.props;
    if (!video) return;
    const { logoProps, textProps, musicProps } = video;

    try {
      if (musicProps && musicProps.url) {
        const res = await fetch(musicProps.url);
        const musicBlob = await res.blob();
        this.backgroundMusic.src = window.URL.createObjectURL(musicBlob);
        this.setState(
          { backgroundMusicUrl: musicProps.url, musicVolume: musicProps.musicVolume?.toString() || "0.5" },
          () => this.syncAudio()
        );
      }
      const response = await fetch(await storageGet(video.url, { level: "protected" }));
      const videoBlob = await response.blob();
      this.video.src = window.URL.createObjectURL(videoBlob);
    } catch (err) {
      console.error("Error loading video:", err);
    }

    if (video.thumbnail) {
      try {
        const thumbUrl = await storageGet(video.thumbnail, { level: "protected" });
        this.setState({ thumbnailPath: video.thumbnail, thumbnailUrl: thumbUrl });
      } catch (e) {
        console.error("Error loading thumbnail:", e);
      }
    }
    if (logoProps) {
      this.setState({
        logoPath: logoProps.url,
        logoUrl: await storageGet(logoProps.url, { level: "protected" }),
        iconPos: logoProps.position,
      });
    }
    if (textProps) {
      this.setState({
        text: textProps.text || "",
        textColor: textProps.textColor || "#ffffff",
        fontSize: textProps.fontSize || 5,
        vAlign: textProps.vAlign || "top",
        align: textProps.align || "left",
      });
    }
    this.setState({ videoLoaded: true });
  };

  UNSAFE_componentWillReceiveProps() {
    this.handleVideoLoaded();
  }

  handleLoadedMetaData = () => {
    const w = this.video.videoWidth || this.video.clientWidth;
    const h = this.video.videoHeight || this.video.clientHeight;
    if (this.canvas) { this.canvas.width = w; this.canvas.height = h; }
    if (this.dummyCanvas) { this.dummyCanvas.width = w; this.dummyCanvas.height = h; }
    if (this.props.video?.logoProps?.position) {
      this.initializeIconPosition(this.props.video.logoProps.position);
    }
    this.updateCanvas();
  };

  // ── Drawing ──

  draw = () => {
    if (!this.video || this.video.paused || this.video.ended) return;
    this.renderFrame();
    this.animFrame = requestAnimationFrame(this.draw);
  };

  renderFrame = () => {
    if (!this.ctx || !this.ctx2 || !this.canvas) return;
    // Don't draw if video has no source loaded yet
    if (!this.video || this.video.readyState < 2) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (w === 0 || h === 0) return;

    this.ctx2.drawImage(this.video, 0, 0, w, h);

    if (this.state.text) {
      this.ctx2.fillStyle = this.state.textColor;
      canvasTxt.fontSize = (this.state.fontSize / 100) * (w - 80);
      canvasTxt.font = "Arial";
      canvasTxt.vAlign = this.state.vAlign;
      canvasTxt.align = this.state.align;
      canvasTxt.justify = false;
      canvasTxt.lineHeight = null;
      canvasTxt.drawText(this.ctx2, this.state.text, 30, 30, w - 50, h - 50);
    }

    // Only draw logo if the image element has fully loaded a valid source
    if (this.img && this.state.logoUrl && this.img.complete && this.img.naturalWidth > 0) {
      const logoDim = 0.15 * w;
      this.ctx2.drawImage(this.img, this.state.logoX as number, this.state.logoY as number, logoDim, logoDim);
    }

    const idata = this.ctx2.getImageData(0, 0, w, h);
    this.ctx.putImageData(idata, 0, 0);
  };

  updateCanvas = () => {
    if (!this.video || !this.ctx || !this.ctx2 || !this.canvas) return;
    this.renderFrame();
  };

  togglePlayPause = () => {
    if (!this.video) return;
    if (this.video.paused) {
      this.video.play().catch(() => {});
    } else {
      this.video.pause();
    }
  };

  onVideoPlay = () => {
    if (this.state.backgroundMusicUrl && this.backgroundMusic) {
      if (this.backgroundMusic.readyState < 3) {
        toast.info("Loading background music...");
      }
      this.backgroundMusic.play().catch(() => {});
    }
    this.setState({ isPlaying: true });
    this.draw();
  };

  onVideoPause = () => {
    if (this.backgroundMusic) this.backgroundMusic.pause();
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    this.setState({ isPlaying: false });
    this.updateCanvas();
  };

  onVideoEnd = () => {
    if (this.backgroundMusic) this.backgroundMusic.currentTime = 0;
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    this.setState({ isPlaying: false });
  };

  syncAudio = () => {
    if (!this.backgroundMusic || !this.video) return;
    const videoVol = this.video.volume * 100;
    this.backgroundMusic.volume = (parseFloat(this.state.musicVolume) / 100) * videoVol;
  };

  // ── Logo ──

  toggleLogoAssetPicker = () => {
    this.setState({ isOpenLogoPicker: !this.state.isOpenLogoPicker });
  };

  onLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) { toast.error("No file selected"); return; }
    if (!e.target.files[0].name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      toast.error("Please select a valid image"); return;
    }
    toast.info("Uploading logo...");
    await this.compress(e.target.files[0]);
  };

  compress(file: any) {
    this.setState({ assetUploading: true });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event: any) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const elem = document.createElement("canvas");
        elem.width = 200;
        elem.height = 200;
        const ctx: any = elem.getContext("2d");
        const scale = Math.min(200 / img.width, 200 / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (200 - w) / 2, (200 - h) / 2, w, h);
        ctx.canvas.toBlob(
          async (blob: any) => {
            await this.saveLogo(blob);
            this.setState({ assetUploading: false });
            toast.info("Logo uploaded");
          },
          file.type,
          1
        );
      };
    };
  }

  saveLogo = (logoBlob: any) => {
    return new Promise<void>((resolve, reject) => {
      storagePut(`${uuid()}-logo`, logoBlob, { level: "protected" })
        .then((response: any) => {
          storageGet(response.key, { level: "protected" }).then((_response: any) => {
            this.setState({ logoPath: response.key, logoUrl: _response }, () => {
              setTimeout(() => this.updateCanvas(), 500);
            });
          });
          this.props.addAsset({ type: "logo", url: response.key });
          resolve();
        })
        .catch((error: any) => {
          toast.error("Failed to upload logo");
          this.setState({ assetUploading: false });
          reject(error);
        });
    });
  };

  onLogoAssetPick = (path: any) => {
    storageGet(path, { level: "protected" }).then((_response: any) => {
      this.setState({ logoPath: path, logoUrl: _response }, () => this.updateCanvas());
      toast.info("Logo selected");
    });
  };

  removeLogo = () => {
    this.setState({ logoPath: null, logoUrl: null, iconPos: "top-left", logoX: 10, logoY: 10 }, () => {
      this.updateCanvas();
      toast.info("Logo removed");
    });
  };

  setIconPosition = (position: string) => {
    if (!this.state.logoPath) { toast.info("Please upload a logo first"); return; }
    if (!this.canvas) return;
    this.setState({ iconPos: position });
    const w = this.canvas.width;
    const h = this.canvas.height;
    const logoDim = 0.15 * w;
    const pad = 20;
    const positions: any = {
      "top-left": { x: pad, y: pad },
      "top-right": { x: w - logoDim - pad, y: pad },
      "bottom-left": { x: pad, y: h - logoDim - pad },
      "bottom-right": { x: w - logoDim - pad, y: h - logoDim - pad },
    };
    const pos = positions[position] || positions["top-left"];
    this.setState({ logoX: pos.x, logoY: pos.y }, () => this.updateCanvas());
  };

  initializeIconPosition = (position: string) => {
    if (!this.canvas) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const logoDim = 0.15 * w;
    const pad = 20;
    const positions: any = {
      "top-left": { x: pad, y: pad },
      "top-right": { x: w - logoDim - pad, y: pad },
      "bottom-left": { x: pad, y: h - logoDim - pad },
      "bottom-right": { x: w - logoDim - pad, y: h - logoDim - pad },
    };
    const pos = positions[position] || positions["top-left"];
    this.setState({ logoX: pos.x, logoY: pos.y });
  };

  // ── Text / Captions ──

  changeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ text: e.target.value }, () => this.updateCanvas());
  };

  handleChangeColor = (color: any) => {
    this.setState({ textColor: color.hex }, () => this.updateCanvas());
  };

  changeFontSize = (_: any, val: any) => {
    this.setState({ fontSize: Number(val) }, () => this.updateCanvas());
  };

  setTextPosition = (position: string) => {
    const map: any = {
      "top-left": { align: "left", vAlign: "top" },
      "top-right": { align: "right", vAlign: "top" },
      "bottom-left": { align: "left", vAlign: "bottom" },
      "bottom-right": { align: "right", vAlign: "bottom" },
      center: { align: "center", vAlign: "middle" },
      "center-bottom": { align: "center", vAlign: "bottom" },
    };
    const pos = map[position] || map["center"];
    this.setState({ align: pos.align, vAlign: pos.vAlign }, () => this.updateCanvas());
  };

  // ── Music ──

  toggleMusicAssetPicker = () => {
    this.setState({ isOpenMusicPicker: !this.state.isOpenMusicPicker });
  };

  onMusicInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audioTypes = /(\.|\/)(mp3|ogg|wav)$/i;
    const musicFile = e.target.files?.[0];
    if (!musicFile) return;
    if (!audioTypes.test(musicFile.name)) { toast.error("Please upload a valid audio file"); return; }
    toast("Add a title and click upload", { autoClose: 3000, transition: Flip });
    this.setState({ musicFileSelected: true, musicFile: musicFile });
  };

  uploadAndSaveMusicAsset = () => {
    if (!this.state.musicTitle) { toast.error("Please add a title"); return; }
    toast.info("Uploading music...");
    this.setState({ assetUploading: true });
    storagePut(`${uuid()}-music`, this.state.musicFile, { level: "protected" })
      .then((response: any) => {
        toast.info("Music uploaded");
        this.setState({
          backgroundMusicUrl: response.key,
          musicFile: null,
          musicFileSelected: false,
          assetUploading: false,
        });
        this.props.addMusicAsset({ url: response.key, title: this.state.musicTitle });
      })
      .catch((error: any) => {
        console.error("Error uploading music:", error);
        toast.error("Failed to upload music");
        this.setState({ assetUploading: false });
      });
  };

  onMusicAssetPick = (path: any, type: string) => {
    storageGet(path, { level: type as any }).then((response: string) => {
      this.setState({ backgroundMusicUrl: response, backgroundMusicKey: path, backgroundMusicType: type });
      toast.info("Loading music...");
      this.setState({
        musicLoadingTimeout: setInterval(() => {
          if (this.backgroundMusic?.readyState === 4) {
            clearInterval(this.state.musicLoadingTimeout);
            this.setState({ musicLoadingTimeout: null });
            toast.info("Music ready");
          }
        }, 2000),
      });
    });
  };

  onChangeMusicTitle = (e: any) => { this.setState({ musicTitle: e.target.value }); };

  onAdjustMusicVolume = (_: any, val: any) => {
    if (this.backgroundMusic) this.backgroundMusic.volume = val;
    this.setState({ musicVolume: String(val) });
  };

  // ── Thumbnail ──

  captureThumbnail = () => {
    if (!this.video || this.video.readyState < 2) {
      toast.error("Video not ready yet. Please wait for it to load.");
      return;
    }
    // Render the current frame with all overlays (logo + text) onto dummyCanvas
    this.renderFrame();

    const thumbCanvas = document.createElement("canvas");
    const w = this.video.videoWidth || 1280;
    const h = this.video.videoHeight || 720;
    thumbCanvas.width = w;
    thumbCanvas.height = h;
    const ctx = thumbCanvas.getContext("2d");
    if (!ctx) return;

    // Draw from the composited canvas (which has video + logo + text overlays)
    if (this.canvas && this.canvas.width > 0 && this.canvas.height > 0) {
      ctx.drawImage(this.canvas, 0, 0, w, h);
    } else {
      // Fallback to raw video frame if canvas isn't ready
      ctx.drawImage(this.video, 0, 0, w, h);
    }
    thumbCanvas.toBlob(
      (blob: any) => {
        if (!blob) { toast.error("Failed to capture frame"); return; }
        if (this.state.thumbnailUrl) URL.revokeObjectURL(this.state.thumbnailUrl);
        const previewUrl = URL.createObjectURL(blob);
        this.setState({ thumbnailUrl: previewUrl, thumbnailPath: blob });
        toast.success("Thumbnail captured from current frame");
      },
      "image/jpeg",
      0.92
    );
  };

  removeThumbnail = () => {
    if (this.state.thumbnailUrl && typeof this.state.thumbnailUrl === "string" && this.state.thumbnailUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.state.thumbnailUrl);
    }
    this.setState({ thumbnailPath: null, thumbnailUrl: null });
    toast.info("Thumbnail removed");
  };

  // ── Save ──

  updateVideoLogoText = async () => {
    try {
      const video: any = {};
      const textProps = {
        text: this.state.text,
        textColor: this.state.textColor,
        fontSize: this.state.fontSize,
        vAlign: this.state.vAlign,
        align: this.state.align,
      };
      if (!_.isNil(this.state.text)) video.textProps = textProps;

      const logoProps = { url: this.state.logoPath, position: this.state.iconPos };
      if (!_.isNil(this.state.logoPath)) video.logoProps = logoProps;

      const musicProps = {
        url: this.state.backgroundMusicKey,
        type: this.state.backgroundMusicType,
        musicVolume: parseFloat(this.state.musicVolume),
      };
      if (!_.isNil(this.state.backgroundMusicUrl)) video.musicProps = musicProps;

      if (this.state.thumbnailPath instanceof Blob) {
        const key = `${uuid()}-thumbnail`;
        const response: any = await storagePut(key, this.state.thumbnailPath, { level: "protected", contentType: "image/jpeg" });
        video.thumbnail = response.key;
      } else if (this.state.thumbnailPath === null && this.props.video?.thumbnail) {
        video.thumbnail = "";
      }

      if (this.props.video) {
        video.id = this.props.video._id;
        this.props.updateVideo(video);
        this.props.cleanSingleVideo();
        this.handleClose();
      }
    } catch (error) {
      toast.info("Error updating");
    }
  };

  musicUpload = () => { toast.error("Please click Upload first"); };

  removeListeners = () => {
    if (this.video && this.video.removeEventListener) {
      this.video.removeEventListener("canplaythrough", this.handleLoadedMetaData);
      this.video.removeEventListener("play", this.onVideoPlay);
      this.video.removeEventListener("pause", this.onVideoPause);
      this.video.removeEventListener("ended", this.onVideoEnd);
      this.video.removeEventListener("volumechange", this.syncAudio);
    }
  };

  handleClose = () => { this.props.toggle(); };

  render() {
    const { type } = this.props;
    const { videoLoaded, backgroundMusicUrl, isPlaying } = this.state;

    return ReactDOM.createPortal(
      <div className="editorOverlay">
        <div className="editorModal">
          <div className="editorHeader">
            <span className="editorHeaderTitle">
              {type === "Logo" ? "Edit Logo" : type === "Text" ? "Edit Captions" : type === "Thumbnail" ? "Set Thumbnail" : "Edit Music"}
            </span>
            <button className="editorCloseBtn" onClick={this.handleClose}>&times;</button>
          </div>

          <div className="editorBody">
            <div className="editorPreview">
              {!videoLoaded && <div className="editorLoading"><Loading /></div>}

              {/* Video is visible when NOT playing; canvas takes over during playback */}
              <div className="editorVideoWrap">
                <video
                  ref={this.video}
                  controls={videoLoaded && !isPlaying}
                  className="editorVideo"
                  style={{ display: isPlaying ? "none" : "block" }}
                />
                <canvas
                  ref={(ref) => { this.canvas = ref; }}
                  className="editorCanvas"
                  style={{ display: isPlaying ? "block" : "none" }}
                />
              </div>
              <canvas ref={(ref) => { this.dummyCanvas = ref; }} style={{ display: "none" }} />
              <img
                crossOrigin="anonymous"
                alt=""
                src={this.state.logoUrl || undefined}
                style={{ display: "none" }}
                ref={(ref) => { this.img = ref; }}
                onLoad={() => this.updateCanvas()}
              />
              <audio
                src={backgroundMusicUrl || undefined}
                ref={(ref) => { this.backgroundMusic = ref; }}
                loop
                style={{ display: "none" }}
              />

              {videoLoaded && (
                <button className="editorPlayBtn" onClick={this.togglePlayPause}>
                  {isPlaying ? "⏸ Pause" : "▶ Play with Overlay Preview"}
                </button>
              )}
            </div>

            <div className="editorControls">
              {type === "Logo" && (
                <div className="editorPanel">
                  <h4 className="editorPanelTitle">Logo</h4>

                  <AssetPicker
                    isOpen={this.state.isOpenLogoPicker}
                    toggle={this.toggleLogoAssetPicker}
                    onPick={this.onLogoAssetPick}
                    logoAssets={true}
                  />
                  <input
                    type="file"
                    onChange={this.onLogoFileChange}
                    ref={(ref) => { this.logoRef = ref; }}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: "none" }}
                  />
                  {this.state.assetUploading && <LinearProgress style={{ marginBottom: 8 }} />}

                  <div className="editorBtnRow">
                    <button className="editorActionBtn editorUploadBtn" onClick={() => this.logoRef?.click()}>
                      Upload Logo
                    </button>
                    <button className="editorActionBtn editorSelectBtn" onClick={this.toggleLogoAssetPicker}>
                      From Assets
                    </button>
                  </div>

                  {this.state.logoUrl && (
                    <div className="editorCurrentLogo">
                      <p className="editorCurrentLogoLabel">Current Logo</p>
                      <img src={this.state.logoUrl} alt="current logo" className="editorLogoThumb" />
                      <button className="editorRemoveLogoBtn" onClick={this.removeLogo}>
                        Remove Logo
                      </button>
                    </div>
                  )}

                  <h5 className="editorSubTitle">Position</h5>
                  <div className="editorPosGrid">
                    {(["top-left", "top-right", "bottom-left", "bottom-right"] as const).map((pos) => (
                      <button
                        key={pos}
                        className={`editorPosBtn ${this.state.iconPos === pos ? "active" : ""}`}
                        onClick={() => this.setIconPosition(pos)}
                      >
                        {pos.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {type === "Text" && (
                <div className="editorPanel">
                  <h4 className="editorPanelTitle">Captions</h4>

                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Caption Text"
                    value={this.state.text}
                    onChange={this.changeText}
                    multiline
                    rows={3}
                    style={{ marginBottom: 16 }}
                  />

                  <h5 className="editorSubTitle">Position</h5>
                  <div className="editorPosGrid editorPosGrid6">
                    {(["center", "center-bottom", "top-left", "top-right", "bottom-left", "bottom-right"] as const).map((pos) => {
                      const isActive =
                        (pos === "center" && this.state.align === "center" && this.state.vAlign === "middle") ||
                        (pos === "center-bottom" && this.state.align === "center" && this.state.vAlign === "bottom") ||
                        (pos === "top-left" && this.state.align === "left" && this.state.vAlign === "top") ||
                        (pos === "top-right" && this.state.align === "right" && this.state.vAlign === "top") ||
                        (pos === "bottom-left" && this.state.align === "left" && this.state.vAlign === "bottom") ||
                        (pos === "bottom-right" && this.state.align === "right" && this.state.vAlign === "bottom");
                      return (
                        <button
                          key={pos}
                          className={`editorPosBtn ${isActive ? "active" : ""}`}
                          onClick={() => this.setTextPosition(pos)}
                        >
                          {pos.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                        </button>
                      );
                    })}
                  </div>

                  <h5 className="editorSubTitle">Font Size</h5>
                  <div className="editorSliderRow">
                    <Slider
                      value={this.state.fontSize}
                      min={1}
                      max={10}
                      step={0.1}
                      onChange={this.changeFontSize}
                      style={{ color: "#fdb415", flex: 1 }}
                    />
                    <span className="editorSliderVal">{Math.round((this.state.fontSize / 10) * 100)}%</span>
                  </div>

                  <h5 className="editorSubTitle">Text Color</h5>
                  <CompactPicker color={this.state.textColor} onChangeComplete={this.handleChangeColor} />
                </div>
              )}

              {type === "Music" && (
                <div className="editorPanel">
                  <h4 className="editorPanelTitle">Background Music</h4>

                  <MusicAssetPicker
                    isOpen={this.state.isOpenMusicPicker}
                    toggle={this.toggleMusicAssetPicker}
                    onPick={this.onMusicAssetPick}
                  />
                  <input
                    type="file"
                    onChange={this.onMusicInputChange}
                    ref={(ref) => { this.musicRef = ref; }}
                    accept="audio/*"
                    style={{ display: "none" }}
                  />
                  {this.state.assetUploading && <LinearProgress style={{ marginBottom: 8 }} />}

                  <div className="editorBtnRow">
                    {!this.state.musicFileSelected ? (
                      <button className="editorActionBtn editorUploadBtn" onClick={() => this.musicRef?.click()}>
                        Select File
                      </button>
                    ) : (
                      <button className="editorActionBtn editorUploadBtn" onClick={this.uploadAndSaveMusicAsset}>
                        Upload
                      </button>
                    )}
                    <button className="editorActionBtn editorSelectBtn" onClick={this.toggleMusicAssetPicker}>
                      From Assets
                    </button>
                  </div>

                  {this.state.musicFileSelected && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      label="Music Title"
                      value={this.state.musicTitle}
                      onChange={this.onChangeMusicTitle}
                      style={{ marginTop: 12 }}
                    />
                  )}

                  {this.state.backgroundMusicUrl && (
                    <>
                      <h5 className="editorSubTitle">Volume</h5>
                      <div className="editorSliderRow">
                        <Slider
                          value={parseFloat(this.state.musicVolume)}
                          min={0}
                          max={1}
                          step={0.05}
                          onChange={this.onAdjustMusicVolume}
                          style={{ color: "#fdb415", flex: 1 }}
                        />
                        <span className="editorSliderVal">{Math.round(parseFloat(this.state.musicVolume) * 100)}%</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {type === "Thumbnail" && (
                <div className="editorPanel">
                  <h4 className="editorPanelTitle">Thumbnail</h4>

                  <p className="editorThumbnailHint">
                    Pause the video at the frame you want, then click "Capture Frame" to set it as your thumbnail.
                  </p>

                  <div className="editorBtnRow">
                    <button
                      className="editorActionBtn editorCaptureBtn"
                      onClick={this.captureThumbnail}
                      disabled={!videoLoaded}
                    >
                      Capture Frame
                    </button>
                    {this.state.thumbnailUrl && (
                      <button className="editorActionBtn editorRemoveThumbBtn" onClick={this.removeThumbnail}>
                        Remove
                      </button>
                    )}
                  </div>

                  {this.state.thumbnailUrl ? (
                    <div className="editorThumbnailPreview">
                      <p className="editorCurrentLogoLabel">New Thumbnail</p>
                      <img src={this.state.thumbnailUrl} alt="captured thumbnail" className="editorThumbnailImg" />
                    </div>
                  ) : (
                    <div className="editorThumbnailEmpty">
                      <p>No thumbnail captured yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="editorFooter">
            <button className="editorCancelBtn" onClick={this.handleClose}>Cancel</button>
            <button
              className="editorSaveBtn"
              onClick={!this.state.musicFileSelected ? this.updateVideoLogoText : this.musicUpload}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
}

const mapStateToProps = (state: any) => ({
  isVideoUpdating: state.video.isVideoUpdating,
  auth: state.auth,
});

const mapDispatchToProps = (dispatch: any) => ({
  updateVideo: (video: any) => dispatch(updateVideo(video)),
  addAsset: (asset: any) => dispatch(addAsset(asset)),
  addMusicAsset: (asset: any) => dispatch(addMusicAsset(asset)),
  cleanSingleVideo: () => dispatch(cleanSingleVideo()),
});

export default connect(mapStateToProps, mapDispatchToProps)(VideoEditor);
